import * as _ from 'lodash';
import * as utils from '../utils';
import * as perf from '../commons/performance-logger';
import * as desiredCapabilitiesBuilder from '../commons/testimDesiredCapabilitiesBuilder';
import pRetry from 'p-retry';
import parser from 'ua-parser-js';
import { W3C_ELEMENT_ID } from './constants';
import { getLogger } from '../commons/logger';
import { isOldProtocol } from './webDriverUtils';
import { WebdriverioWebDriverApi as WebDriverApi } from './WebdriverioWebDriverApi';
import { InvalidGridCredentials, SeleniumCrashError, WebdriverCapabilitiesError } from '../errors';
import { featureFlagsService } from '../commons/featureFlagsService';
import { SELENIUM_PERF_MARKS } from '../commons/SeleniumPerfStats';
import type { ChainablePromiseElement } from 'webdriverio';
import type { RunnerMode } from '../runners/runnerUtils';
import type { SeleniumPerfStats } from '../commons/SeleniumPerfStats';
import type { Capabilities } from '@wdio/types';
import type { ProjectData, RunnerOptions } from '../runOptions';
import type { TabService } from './services/tabService';
import type { SerializedGrid } from '../services/gridService';
import type { SeleniumTarget } from './stepActions/stepAction';
import type { LambdatestService } from '../services/lambdatestService';
import type { DnDRectsAndOffsets, DoubleClickEventData } from './stepActions/mouseStepAction';
import type { ISerializedStep } from 'clickim/src/common/models/step';
import type { LocateStep } from 'clickim/src/common/models/locateStep';
import type { VisibilityInfo } from 'clickim/src/background/visibilityUtils';
import type { ILocatedElement } from 'clickim/src/background/codeSnippets/getLocatedElement';
import type { ICapabilities } from 'services/src/commons/mongo/models/customCapabilities';
import type { TestRunHandler } from 'src/testRunHandler';

type RemoteOptions = Capabilities.RemoteConfig;

const logger = getLogger('webdriver');
const [LEFT, RIGHT] = [0, 2];
const { extractElementId, getCdpAddressForHost } = utils;

const codeSnippets = () => {
    const sessionPlayer = (require('../commons/getSessionPlayerRequire') as typeof import('../commons/getSessionPlayerRequire')).getSessionPlayer();
    return sessionPlayer.codeSnippets;
};
const locatorBuilderUtils = () => {
    const sessionPlayer = (require('../commons/getSessionPlayerRequire') as typeof import('../commons/getSessionPlayerRequire')).getSessionPlayer();
    return sessionPlayer.locatorBuilderUtils;
};
const playerUtils = () => {
    const sessionPlayer = (require('../commons/getSessionPlayerRequire') as typeof import('../commons/getSessionPlayerRequire')).getSessionPlayer();
    return sessionPlayer.utils;
};
const positionUtils = () => {
    const sessionPlayer = (require('../commons/getSessionPlayerRequire') as typeof import('../commons/getSessionPlayerRequire')).getSessionPlayer();
    return sessionPlayer.positionUtils;
};

async function getCdpAddress(initResult?: unknown) {
    try {
        // @ts-expect-error initResult seems to always be void
        const debuggerHost = initResult?.value?.['goog:chromeOptions']?.debuggerAddress;
        if (!debuggerHost) {
            return undefined;
        }
        return await getCdpAddressForHost(debuggerHost);
    } catch (e) {
        logger.info('Error getting cdpAddress', e);
        return undefined;
    }
}

export interface ActionOffsets {
    frameOffset: { x: number; y: number };
    clickOffset: { x: number; y: number };
    rect: VisibilityInfo;
}

export interface InitOptions {
    projectData?: ProjectData;
    overrideConfiguration?: any;
    browserOptions: RunnerOptions;
    testId?: string;
    testName: string;
    testRunConfig: TestRunHandler['runConfig'];
    gridInfo: SerializedGrid;
    customExtensionLocalLocation: string | undefined;
    executionId: string;
    executionName?: string;
    testResultId: string;
    seleniumPerfStats?: SeleniumPerfStats;
    fastInit?: boolean;
    lambdatestService?: LambdatestService;
    printFinalCaps?: boolean;
    mode?: RunnerMode;
    customCapabilities?: ICapabilities;
    useLocalChromeDriver?: boolean;
}

export class WebDriver extends WebDriverApi {
    public tabService?: TabService;
    public cdpUrl?: string;
    private started = false;
    private keepAliveTimer: NodeJS.Timeout | null = null;
    private _isAlive = false;
    private _keepAliveRequests: { start: number; id: string; end?: number; error?: number }[] = [];
    private browserClosedCallbacks: Array<(err: any) => any> = [];
    private browserClosedFailedKeepAlives = 0;
    private keepAliveFailedCallbacks: Array<(err: any) => any> = [];
    private generalKeepAliveFailures = 0;
    private ignoreHiddenTagsText = false;
    public browserAndOS: { os: string; browserMajor: number; browser: string; userAgent: string; browserVersion?: string } | null = null;
    private ignoreVisibility?: boolean;

    public getIgnoreVisibility() {
        return this.ignoreVisibility;
    }

    public setIgnoreVisibility(shouldIgnore: boolean) {
        this.ignoreVisibility = shouldIgnore;
    }

    public registerToClosedBrowser(callback: (err: any) => any) {
        this.browserClosedCallbacks.push(callback);
    }

    public unregisterToClosedBrowser(callback: (err: any) => any) {
        this.browserClosedCallbacks = this.browserClosedCallbacks.filter(cb => cb !== callback);
    }

    public registerToKeepAliveFailed(callback: (err: any) => any) {
        this.keepAliveFailedCallbacks.push(callback);
    }

    public unregisterToKeepAliveFailed(callback: (err: any) => any) {
        this.keepAliveFailedCallbacks = this.keepAliveFailedCallbacks.filter(cb => cb !== callback);
    }

    public async init(options: InitOptions) {
        // Deconstructing the options object to avoid passing it to the constructor
        const {
            projectData,
            overrideConfiguration,
            browserOptions,
            testId,
            testName,
            testRunConfig,
            gridInfo,
            customExtensionLocalLocation,
            executionId,
            executionName,
            testResultId,
            seleniumPerfStats,
            fastInit,
            lambdatestService,
            printFinalCaps,
            mode,
            customCapabilities,
            useLocalChromeDriver,
        } = options;

        this.browserClosedFailedKeepAlives = 0;
        this.ignoreHiddenTagsText = Boolean(browserOptions?.company?.activePlan?.premiumFeatures?.ignoreHiddenTagsText);
        this.browserClosedCallbacks = [];

        const capabilities = desiredCapabilitiesBuilder.buildSeleniumOptions({
            projectData,
            browserOptions,
            testId,
            testName,
            testRunConfig,
            gridInfo,
            customExtensionLocalLocation,
            executionId,
            executionName,
            testResultId,
            lambdatestService,
            overrideConfiguration,
            printFinalCaps,
            mode,
            customCapabilities,
            useLocalChromeDriver
        });
        if ((capabilities as any).desiredCapabilities) {
            delete (capabilities as any).desiredCapabilities.marionette;
        }
        if (capabilities.capabilities) {
            delete (capabilities.capabilities.alwaysMatch as any).marionette;
        }

        this.initUnsupportedActions(lambdatestService?.isLambdatestRun());
        this.browserAndOS = null;
        this.seleniumPerfStats = seleniumPerfStats;
        const driverDelay = fastInit ? 0 : 1500;
        const focus = fastInit ? (() => null) : () => this.executeJS<() => void>('window.focus()');
        try {
            perf.log('before initClient in webdriver.js init');
            // We only need to send the path to the extension to the browser if we are using local Chrome driver
            const extensionsToUploadWithBidi = {
                unzippedTestimExtensionPath: (capabilities as any).unzippedTestimExtensionPath,
                unzippedCustomExtensionPath: (capabilities as any).unzippedCustomExtensionPath,
            }
            const initResult = await this.initClient(capabilities as RemoteOptions, testName, testResultId, useLocalChromeDriver, extensionsToUploadWithBidi);
            perf.log('after initResult before getCdpAddress in init');
            this.cdpUrl = await getCdpAddress(initResult);
            perf.log('after getCdpAddress in webdriver.js init');
            logger.info(`init new session testName: ${testName}`, { sessionId: this.getSessionId(), testResultId });
            await utils.delay(driverDelay);
            if (!featureFlagsService.flags.dontFocusOnInit.isEnabled()) {
                await focus();
            }
            perf.log('after focus and delay in webdriver.js init');
        } catch (err) {
            // Don't log the extension as it's a huge base64 string
            // (webdriver still stringifies the capabilities object in some of the errors 😔)
            const capabilitiesAsString = JSON.stringify(
                _.omit(
                    capabilities,
                    'extensions',
                    'capabilities.extensions',
                    'capabilities.capabilities.extensions',
                    'capabilities.alwaysMatch.goog:chromeOptions.extensions',
                ),
            );
            logger.error('failed to init webdriver', { err, capabilitiesAsString });
            const isFreeCompany = browserOptions?.company?.activePlan?.plan === 'free';
            if (err.stack?.includes('at startWebDriverSession') && err.message?.startsWith('Failed to create session') && err.message?.includes('Timeout awaiting \'request\'') && isFreeCompany) {
                throw new Error('Our free grids are in full capacity, please try again or upgrade to our Professional plan');
            }
            if (/Invalid username or password/.test(err.message)) {
                throw new InvalidGridCredentials(err.message);
            }
            // If WDIO throws an error from Undici (introduced in WDIO 9), we want to ignore capabilities error as it's not a problem with the capabilities.
            if (/capabilit(y|ies)/.test(err.message) && !(/UND_ERR_HEADERS_TIMEOUT/.test(err.message))) {
                throw new WebdriverCapabilitiesError(err.message);
            }
            if (/403 Forbidden/.test(err.message)) {
                throw new Error('Grid Access Denied');
            }
            if (/Browser_Version not supported/.test(err.message)) { // example: "Browser/Browser_Version not supported"
                throw new Error('Browser or Browser version not supported');
            }
            throw new Error(`Failed to init client driver: ${err.message}`);
        }
    }

    private initUnsupportedActions(isLambdatestRun?: boolean) {
        if (!isLambdatestRun) {
            return;
        }
        this.unsupportedActions.add('move');
    }

    public isAlive() {
        return this._isAlive;
    }

    public maxKeepAliveGap() {
        const slidingPairsWindow = (arr: number[]) => _.zip(arr.slice(0, -1), arr.slice(1));
        const startTimeArray = this._keepAliveRequests.map(({ start }) => start).filter(Boolean);
        const timeGaps = slidingPairsWindow(startTimeArray).map(([a, b]) => b! - a!);
        return Math.max(...timeGaps);
    }

    private isClosedBrowserError(err: any) {
        if (['session-not-found', 'no such window', 'invalid session id'].includes(err?.name)) {
            return true;
        }
        const messagesIndicatingBrowserClosed = [
            'CLIENT_STOPPED_SESSION',
            'BROWSER_TIMEOUT',
            'was terminated due to TIMEOUT',
            'window was already closed',
            'chrome not reachable',
            'not connected to DevTools',
        ];
        return messagesIndicatingBrowserClosed.some(msg => err.message.includes(msg));
    }

    public start() {
        if (this.started) {
            return;
        }
        this.started = true;
        const keepAlive = async () => {
            const createKeepAliveRequestData = (id: string) => this._keepAliveRequests.push({ start: Date.now(), id });
            const updateKeepAliveRequestData = (field: 'end' | 'error', id: string) => {
                const req = this._keepAliveRequests.find(item => item.id === id);
                if (!req) {
                    return;
                }
                req[field] = Date.now();
            };

            function checkKeepAliveScript() {
                // @ts-expect-error unhappy with declare in a function
                declare let window: Window & { getTestimStatus?(): string };
                return window.getTestimStatus?.();
            }

            if (this.queue.getQueueLength() > 0) {
                return;
            }

            const requestId = utils.guid();
            createKeepAliveRequestData(requestId);
            try {
                await this.executeJS(checkKeepAliveScript);
                this._isAlive = true;
                updateKeepAliveRequestData('end', requestId);
                this.browserClosedFailedKeepAlives = 0;
                this.generalKeepAliveFailures = 0;
            } catch (err) {
                updateKeepAliveRequestData('error', requestId);
                if (err.name === 'unexpected alert open') {
                    this.browserClosedFailedKeepAlives = 0;
                    this.generalKeepAliveFailures = 0;
                    logger.warn('close unexpected alert open');
                    await this.alertAccept().catch(innerErr => logger.warn('failed to click on alert', { err: innerErr }));
                    return;
                }
                logger.warn('err while getting testim status', { err, testResultId: this.testResultId });
                this._isAlive = false;
                if (this.isClosedBrowserError(err)) {
                    this.browserClosedFailedKeepAlives++;
                    const CLOSED_BROWSER_THRESHOLD_COUNT = 3;
                    logger.warn('browser was closed', {
                        err,
                        testResultId: this.testResultId,
                        counter: this.browserClosedFailedKeepAlives,
                        threshold: CLOSED_BROWSER_THRESHOLD_COUNT,
                        numOfCallbacks: this.browserClosedCallbacks.length,
                    });
                    if (this.browserClosedFailedKeepAlives >= CLOSED_BROWSER_THRESHOLD_COUNT) {
                        this.handleThresholdBasedError(this.browserClosedCallbacks, err);
                    }
                } else {
                    const KEEP_ALIVE_FAILURE_THRESHOLD_COUNT = 3;
                    this.generalKeepAliveFailures++;
                    this.browserClosedFailedKeepAlives = 0;
                    if (this.generalKeepAliveFailures >= KEEP_ALIVE_FAILURE_THRESHOLD_COUNT) {
                        this.handleThresholdBasedError(this.keepAliveFailedCallbacks, err);
                    }
                }
            }
        };

        this.keepAliveTimer = setInterval(keepAlive, 10000);
        this.registerToClosedBrowser(this.stopKeepAliveInterval);
    }

    private handleThresholdBasedError(callbacks: Array<(err: any) => any>, err: any ) {
        callbacks.forEach(cb => {
            try {
                cb(err);
            } catch { /* ignore */ }
        });
    }

    private stopKeepAliveInterval = () => {
        if (!this.keepAliveTimer) {
            return;
        }
        this.unregisterToClosedBrowser(this.stopKeepAliveInterval);
        clearInterval(this.keepAliveTimer);
        this.keepAliveTimer = null;
    };

    public async switchToLocatedFrame(locatedElement: ILocatedElement | string | number) {
        const el = await this.getElement(locatedElement);
        if (!el.value) {
            throw new Error('Element not found');
        }
        const elementId = extractElementId(el.value);
        await this.switchFrame({ ELEMENT: elementId, [W3C_ELEMENT_ID]: elementId });
        return el;
    }

    private switchFrame(el: utils.LegacyElement | utils.W3CElement) {
        return this.frame(el);
    }

    public async switchToTopFrame() {
        try {
            return await this.frame();
        } catch (err) {
            if (err.message?.includes('ECONNREFUSED')) {
                throw new SeleniumCrashError();
            }
            throw err;
        }
    }

    public async getElement(locatedElement: ILocatedElement | string | number): Promise<{ value: ChainablePromiseElement | undefined}> {
        const perfId = this.seleniumPerfStats!.markStart(SELENIUM_PERF_MARKS.GET_ELEMENT);
        if (typeof locatedElement === 'string' || typeof locatedElement === 'number') { // support testimId in the meanwhile for backwards compatibility
            try {
                return await this.getElementBySelector(`[testim_dom_element_id='${locatedElement}']`);
            } finally {
                this.seleniumPerfStats!.markEnd(perfId, SELENIUM_PERF_MARKS.GET_ELEMENT);
            }
        }
        const getElementAtAut = locatedElement?.shadowPath?.length || (featureFlagsService.flags.runGetElementCodeInAut.isEnabled() && this.isSafari());

        if (getElementAtAut) {
            try {
                return await this.execute(`
                        var fn = ${codeSnippets().getLocatedElementCode};
                        return fn.apply(null, arguments);
                    `, locatedElement,
                );
            } finally {
                this.seleniumPerfStats!.markEnd(perfId, SELENIUM_PERF_MARKS.GET_ELEMENT);
            }
        }

        try {
            return await this.getElementBySelector(`[testim_dom_element_id='${locatedElement?.testimId}']`);
        } finally {
            this.seleniumPerfStats!.markEnd(perfId, SELENIUM_PERF_MARKS.GET_ELEMENT);
        }
    }

    public executeJS<T extends(...args: any[]) => any = any>(fn: string | T, ...args: Parameters<T>) {
        return this.execute(fn, ...args);
    }

    public async executeCodeAsync<T = any, TArgs extends any[] = any[]>(fn: string | ((...args: [...innerArgs: TArgs, callback: (result?: T) => void]) => void), timeout: number, ...args: TArgs) {
        await this.timeouts('script', timeout);
        return await this.executeAsync(fn, ...args);
    }

    public async markDynamicParent(target: SeleniumTarget, dynamicParentId: string) {
        function setDynamicParentAttribute({ attributeName, attributeValue, locatedElement }: { attributeName: string; attributeValue: string; locatedElement: ILocatedElement }) {
            // @ts-expect-error unhappy with declare in a function
            declare function getLocatedElement(_locatedElement: ILocatedElement | string): Element | null;
            const element = getLocatedElement(locatedElement);
            if (!element) {
                throw new Error('could not find dynamic parent');
            }
            element.setAttribute(attributeName, attributeValue);
        }

        await this.executeJS<typeof setDynamicParentAttribute>(`
            var getLocatedElement = ${codeSnippets().getLocatedElementCode};
            var setDynamicParentAttribute = ${setDynamicParentAttribute.toString()};
            return setDynamicParentAttribute.apply(null, arguments)
        `, {
            attributeName: locatorBuilderUtils().DYNAMIC_PARENT_FIELD_NAME,
            attributeValue: dynamicParentId,
            locatedElement: target.locatedElement,
        });
    }

    private getLocatedElementRectWithPadding(locatedElement: ILocatedElement) {
        // this is here to conform with clickim's logic in frame offset calculations
        function getLocationCode(_locatedElement: ILocatedElement) {
            // @ts-expect-error unhappy with declare in a function
            declare function getLocatedElement(_locatedEl: ILocatedElement | string): Element | null;
            const element = getLocatedElement(_locatedElement);
            if (!element) {
                return null;
            }
            const paddingTop = parseInt(window.getComputedStyle(element).paddingTop.replace('px', ''), 10) || 0;
            const paddingLeft = parseInt(window.getComputedStyle(element).paddingLeft.replace('px', ''), 10) || 0;
            const rect = element.getBoundingClientRect();
            return { top: Math.round(rect.top + paddingTop), left: Math.round(rect.left + paddingLeft) };
        }

        return this.executeJS<typeof getLocationCode>(`
            const getLocatedElement = ${codeSnippets().getLocatedElementCode};
            const getLocation = ${getLocationCode.toString()};
            return getLocation.apply(null, arguments)
        `, locatedElement);
    }

    public getElementLocationWithPadding(locatedElement: ILocatedElement) {
        return this.getLocatedElementRectWithPadding(locatedElement);
    }

    private getLocatedElementRect(locatedElement: ILocatedElement) {
        function getLocationCode(_locatedElement: ILocatedElement) {
            // @ts-expect-error unhappy with declare in a function
            declare function getLocatedElement(_locatedEl: ILocatedElement | string): Element | null;
            const element = getLocatedElement(_locatedElement);
            if (!element) {
                return null;
            }
            const rect = element.getBoundingClientRect();
            return {
                bottom: Math.round(rect.bottom),
                height: Math.round(rect.height),
                x: Math.round(rect.left),
                right: Math.round(rect.right),
                y: Math.round(rect.top),
                width: Math.round(rect.width),
            };
        }

        return this.executeJS<typeof getLocationCode>(`
            const getLocatedElement = ${codeSnippets().getLocatedElementCode};
            const getLocation = ${getLocationCode.toString()};
            return getLocation.apply(null, arguments)
        `, locatedElement);
    }

    private getElementLocation(target: SeleniumTarget) {
        return this.getLocatedElementRect(target.locatedElement);
    }

    public getTargetText(target: SeleniumTarget) {
        return this.getElementTextJS(target.locatedElement);
    }

    public async getElementTextJS(locatedElement: ILocatedElement) {
        function extractTextCode(_locatedElement: ILocatedElement, ignoreHiddenTagsText: boolean) {
            // copy of utils.getElementTextContent to run inside content script sadly .children doesn't work for SVG elements in IE11
            // TODO: clean this function up - as we don't support IE11 no more
            function clearTitleTags(node: Node) {
                if (!node.childNodes?.length) {
                    return node;
                }

                const children = [...node.childNodes].filter((x): x is Element => x.nodeType === Node.ELEMENT_NODE);
                children.forEach(child => {
                    if (typeof child.tagName === 'string' && child.tagName.toLowerCase() === 'title') {
                        node.removeChild(child);
                    } else {
                        clearTitleTags(child);
                    }
                });
                return node;
            }

            function isTextElement(element: Element): element is HTMLInputElement | HTMLTextAreaElement {
                return (['INPUT', 'TEXTAREA'].includes(element.tagName));
            }

            function getFixedTextContent(element: Element) {
                try { // fix for salesforce's custom-elements
                    if (element.shadowRoot && !Object.getOwnPropertyDescriptor(element.ownerDocument.defaultView?.Node.prototype, 'textContent')?.get?.toString().includes('[native code]')) {
                        return element.shadowRoot.textContent!.replace(/(\r\n|\n|\r)/gm, '');
                    }
                } catch { /* noop */ }
                if (ignoreHiddenTagsText && Array.prototype.some.call(element.children, (elem: HTMLElement) => elem.hidden)) {
                    const dupElement = element.cloneNode(true) as Element;
                    const hiddenChildren = Array.prototype.filter.call<HTMLCollection, [(elem: HTMLElement) => boolean], HTMLElement[]>(dupElement.children, elem => elem.hidden);
                    hiddenChildren.forEach(child => dupElement.removeChild(child));
                    return dupElement.textContent!.replace(/(\r\n|\n|\r)/gm, '');
                }
                return element.textContent!.replace(/(\r\n|\n|\r)/gm, '');
            }

            function getElementTextContent(element: Element) {
                if (isTextElement(element)) {
                    return element.value;
                }
                if (element instanceof SVGElement) {
                    const copyElement = element.cloneNode(true);
                    return clearTitleTags(copyElement).textContent!.replace(/(\r\n|\n|\r)/gm, '');
                }
                return getFixedTextContent(element);
            }

            // @ts-expect-error unhappy with declare in a function
            declare function getLocatedElement(_locatedEl: ILocatedElement | string): Element | null;
            const element = getLocatedElement(_locatedElement);
            return element ? getElementTextContent(element) : '';
        }

        const { value } = await this.executeJS<typeof extractTextCode>(`
            const getLocatedElement = ${codeSnippets().getLocatedElementCode};
            const extractText = ${extractTextCode.toString()};
            return extractText.apply(null, arguments)
        `, locatedElement, this.ignoreHiddenTagsText);
        return value;
    }

    public async getHTML(locateStep: ISerializedStep<LocateStep>) {
        const getHTMLCode = codeSnippets().getHtmlCode(undefined, locateStep);
        const perfId = this.seleniumPerfStats!.markStart(SELENIUM_PERF_MARKS.GET_HTML);
        try {
            const { value } = await this.executeJS(getHTMLCode);
            this.seleniumPerfStats!.markEnd(perfId, SELENIUM_PERF_MARKS.GET_HTML);
            return value;
        } catch (err) {
            this.seleniumPerfStats!.markEnd(perfId, SELENIUM_PERF_MARKS.GET_HTML);
            const testimInternalError = Object.assign(new Error(''), { success: false, reason: err.toString(), errorType: 'internal-error' });
            // @ts-expect-error TODO: seems like this is gone in WDIO7 and is crashing the CLI
            if (!this.client?.requestHandler?.sessionID) {
                // we got here after the driver has been disposed of. It's impossible to run JavaScript on the page.
                Object.assign(testimInternalError, { extraInfo: 'Inside getHtml catch and trying to check if in quirks mode - but the session has already terminated' });
                throw testimInternalError;
            }
            throw testimInternalError;
        }
    }

    public maximizeWithoutValidation() {
        return this.windowHandleMaximize();
    }

    private setViewportSizeNEW(size: { width: number; height: number }, type: boolean) {
        const MAX_TRIES = 5;
        class SizeMismatchError extends Error {}

        function getViewportSizeScript() {
            return {
                screenWidth: Math.floor(window.innerWidth || 0),
                screenHeight: Math.floor(window.innerHeight || 0),
            };
        }

        /**
         * to set viewport size properly we need to execute the process multiple times
         * since the difference between the inner and outer size changes when browser
         * switch between fullscreen modes or visibility of scrollbar
         */
        const _setViewportSize = () => pRetry(async () => {
            /** get window size */
            const { value: windowHandleSize } = await this.windowHandleSize();
            /** get viewport size */
            const { value: viewportSize } = await this.execute(getViewportSizeScript);
            const widthDiff = windowHandleSize.width - viewportSize.screenWidth;
            const heightDiff = windowHandleSize.height - viewportSize.screenHeight;
            /** change window size with indent */
            await this.windowHandleSize({
                width: size.width + widthDiff,
                height: size.height + heightDiff,
            });
            const { value: res } = await this.execute(getViewportSizeScript);
            /** if viewport size not equals desired size, throw an error so pRetry will execute process again */
            if (res.screenWidth !== size.width || res.screenHeight !== size.height) {
                throw new SizeMismatchError();
            }
            return undefined;
        }, { retries: MAX_TRIES - 1, factor: 1, minTimeout: 0 }).catch(err => {
            if (err instanceof SizeMismatchError) {
                return undefined;
            }
            throw err;
        });

        /** parameter check */
        if (
            typeof size !== 'object' ||
            typeof size.width !== 'number' ||
            typeof size.height !== 'number' ||
            (type !== undefined && typeof type !== 'boolean')
        ) {
            throw new Error('number or type of arguments don\'t agree with setViewportSize command');
        }

        const shouldIndent = type === undefined ? true : type;

        return shouldIndent ? _setViewportSize() : this.windowHandleSize(size);
    }

    public setViewportSize(width: number, height: number) {
        const type = true; // set to false to change window size, true (default) to change viewport size
        return this.setViewportSizeNEW({ width, height }, type);
    }

    private getBrowserMajorVersion(parse: parser.IResult) {
        try {
            return parseInt(parse.browser?.version || '0', 10);
        } catch (err) {
            logger.error('failed to get browser version', { err });
            return 0;
        }
    }

    public async getBrowserAndOS() {
        function getBrowserName(ua: string) {
            let M = ua.match(/(opera|chrome|safari|firefox)\/?\s*(\d+)/i) || [];
            if (M[1] === 'Chrome' && ua.match(/\bOPR\/(\d+)/) !== null) {
                return 'opera';
            }
            if (M[1] === 'Chrome' && ua.match(/\bEdge|Edg\/(\d+)/) !== null) {
                return 'edge';
            }
            // @ts-expect-error no idea how this could work
            M = M[2] ? [M[1], M[2]] : [ua.appName, ua.appVersion, '-?'];
            const tem = ua.match(/version\/(\d+)/i);
            if (tem !== null) {
                M.splice(1, 1, tem[1]);
            }
            return M[0].toLowerCase();
        }

        if (this.browserAndOS) {
            return this.browserAndOS;
        }

        function getUserAgentString() {
            if (!window?.navigator?.userAgent) {
                return 'unknown';
            }
            return window.navigator.userAgent;
        }
        const { value: userAgent } = await this.executeJS(getUserAgentString);
        const parse = parser(userAgent);
        const osDetails = parse.os;
        this.browserAndOS = {
            os: `${osDetails.name} ${osDetails.version}`,
            browserMajor: this.getBrowserMajorVersion(parse),
            browser: getBrowserName(userAgent),
            userAgent,
            browserVersion: parse.browser.version,
        };
        return this.browserAndOS;
    }

    public setValue(element: utils.W3CElement | utils.LegacyElement, value: string | string[]) {
        return super.setValue(element, value);
    }

    private getRelativeMoveActions(offsets: ActionOffsets, element: utils.W3CElement | utils.LegacyElement) {
        const { rect, clickOffset } = offsets;
        const elementCenter = positionUtils().calculateElementMiddlePoint(rect);

        // here offset values should be in relation to the center of the element
        const left = clickOffset.x;
        const top = clickOffset.y;

        if (
            !playerUtils().isWithinBounds(rect.left, rect.left + rect.width, elementCenter.x + left) ||
            !playerUtils().isWithinBounds(rect.top, rect.top + rect.height, elementCenter.y + top)
        ) {
            // [NOTE] the code is not supposed to get here! - using center of element (0,0) instead of step offsets.
            // this is a fallback so the action will take place even if for some reason calculation went out of element..
            logger.warn('using element center as fallback for offset');
            return this.getMoveActions(0, 0, element);
        }
        return this.getMoveActions(left, top, element);
    }

    private computeAbsoluteMovement(offsets: ActionOffsets) {
        const { frameOffset, rect, clickOffset } = offsets;
        const fallbackLeft = rect.left + clickOffset.x + frameOffset.x;
        const fallbackTop = rect.top + clickOffset.y + frameOffset.y;
        return { x: fallbackLeft, y: fallbackTop };
    }

    private async actWithActionsAPI(offsets: ActionOffsets, button: number, element: utils.W3CElement | utils.LegacyElement, actions: string[]) {
        const moveActions = this.getRelativeMoveActions(offsets, element);
        const clickActions = this.getClickActions(actions, button);
        try {
            return await this.actions([{
                type: 'pointer',
                id: 'mouse',
                parameters: { pointerType: 'mouse' },
                actions: moveActions.concat(clickActions),
            }]);
        } catch (err) {
            logger.error('tried to use element origin but failed because of visibility, trying absolute', err);
            const { x, y } = this.computeAbsoluteMovement(offsets);
            const _moveActions = this.getMoveActions(x, y);
            return this.actions([{
                type: 'pointer',
                id: 'mouse',
                parameters: { pointerType: 'mouse' },
                actions: _moveActions.concat(clickActions),
            }]);
        }
    }

    private doubleClickWithActionsAPI(element: utils.W3CElement | utils.LegacyElement, offsets: ActionOffsets) {
        return this.actWithActionsAPI(offsets, LEFT, element, ['pointerDown', 'pointerUp', 'pointerDown', 'pointerUp']);
    }

    private async doubleClickWithJS(eventData: DoubleClickEventData) {
        const [{ doubleClick }, { dispatchFocus }] = await Promise.all([import('./stepActions/scripts/doubleClick'), import('./stepActions/scripts/focusElement')]);
        type CallbackReturnType = Parameters<Parameters<typeof doubleClick>[1]>[0];
        return this.executeCodeAsync<CallbackReturnType, [DoubleClickEventData]>(`
            var getLocatedElement = ${codeSnippets().getLocatedElementCode};
            var dispatchFocus = ${dispatchFocus.toString()};
            var doubleClick = ${doubleClick.toString()};
            var eventData = arguments[0];
            var done = arguments[1];
            return doubleClick.call(null, eventData, done);
        `, eventData.timeout, eventData);
    }

    private getClickActions(types: string[] = [], button: number | undefined = undefined) {
        return types.map(type => ({ type, button }));
    }

    private getClickActionList(types: string[] = [], button: number | undefined = undefined) {
        return [{
            type: 'pointer',
            id: 'mouse',
            actions: this.getClickActions(types, button),
        }];
    }

    private leftClickWithActionsAPI(element: utils.W3CElement | utils.LegacyElement, offsets: ActionOffsets) {
        return this.actWithActionsAPI(offsets, LEFT, element, ['pointerDown', 'pointerUp']);
    }

    private rightClickWithActionsAPI(element: utils.W3CElement | utils.LegacyElement, offsets: ActionOffsets) {
        return this.actWithActionsAPI(offsets, RIGHT, element, ['pointerDown', 'pointerUp']);
    }

    public async rightClick(element: utils.W3CElement | utils.LegacyElement, offsets: ActionOffsets) {
        if (this.unsupportedActions.has('move')) {
            return this.rightClickWithActionsAPI(element, offsets);
        }
        try {
            return await super._rightClick(element as utils.W3CElement, offsets);
        } catch (err) {
            if (isOldProtocol(err)) {
                this.unsupportedActions.add('move');
                return this.rightClickWithActionsAPI(element, offsets);
            }
            throw err;
        }
    }

    public async leftClick(element: utils.W3CElement | utils.LegacyElement, offsets: ActionOffsets) {
        if (this.unsupportedActions.has('move')) {
            return this.leftClickWithActionsAPI(element, offsets);
        }
        try {
            return await super._leftClick(element as utils.W3CElement, offsets);
        } catch (err) {
            if (isOldProtocol(err)) {
                this.unsupportedActions.add('move');
                return this.leftClickWithActionsAPI(element, offsets);
            }
            throw err;
        }
    }

    public async hover(seleniumElement: utils.W3CElement | utils.LegacyElement, offsets: ActionOffsets) {
        // this will attempt to use the old api with moveTOObject (using the selector)
        // if the old api is unsupported, it will move absolutePoint, or calc the point using the seleniumElement and offset
        if (this.unsupportedActions.has('move')) {
            return this.moveToElementWithActionsAPI(seleniumElement, offsets);
        }

        const { x, y } = offsets.clickOffset;
        try {
            return await this.moveTo(seleniumElement as utils.W3CElement, x, y);
        } catch (err) {
            if (isOldProtocol(err)) {
                this.unsupportedActions.add('move');
                return this.moveToElementWithActionsAPI(seleniumElement, offsets);
            }
            throw err;
        }
    }

    private getMoveActions(x = 1, y = 1, origin: any = 'viewport', duration = 0): object[] {
        // force x != 0 ,y != 0 because of Safari issues
        return [{ type: 'pointerMove', duration, x: Math.floor(x) || 1, y: Math.floor(y) || 1, origin }];
    }

    private moveWithActionsAPI(point: { x: number; y: number }) {
        const actions = this.getMoveActions(point.x, point.y);
        return this.actions([{
            type: 'pointer',
            id: 'mouse',
            actions,
        }]);
    }

    private async moveToElementWithActionsAPI(seleniumElement: utils.W3CElement | utils.LegacyElement, offsets: ActionOffsets) {
        try {
            return await this.actions([{
                type: 'pointer',
                id: 'mouse',
                actions: this.getRelativeMoveActions(offsets, seleniumElement),
            }]);
        } catch (err) {
            logger.error('tried to use element origin but failed because of visibility, trying location', err);
            const point = this.computeAbsoluteMovement(offsets);
            return await this.moveWithActionsAPI(point);
        }
    }

    private getMoveRelativeSequence(startLeft: number, startTop: number, endLeft: number, endTop: number) {
        const distance = (p1: { x: number; y: number }, p2: { x: number; y: number }) => Math.hypot(p1.x - p2.x, p1.y - p2.y);

        const from = { x: startLeft, y: startTop };
        const to = { x: endLeft, y: endTop };
        const MOVE_EVENT_DIST = 10;
        const eventCount = Math.round(distance(from, to) / MOVE_EVENT_DIST);

        /* generate move events array */
        const moves = Array.from({ length: eventCount }, () => ({
            x: Math.round((to.x - from.x) / eventCount),
            y: Math.round((to.y - from.y) / eventCount),
        }));
        return [{ x: 1, y: 1 }].concat(moves);
    }

    private getMoveAbsoluteSequence(startLeft: number, startTop: number, endLeft: number, endTop: number) {
        const relativeMoves = this.getMoveRelativeSequence(startLeft, startTop, endLeft, endTop);
        return relativeMoves.reduce((newMoves, relativeMove) => {
            const lastMove = newMoves.at(-1)!;
            return newMoves.concat({ x: Math.round(lastMove.x + relativeMove.x), y: Math.round(lastMove.y + relativeMove.y) });
        }, [{ x: Math.round(startLeft), y: Math.round(startTop) }]);
    }

    private dragAndDropWithActionsAPIWithGeneratedMoves(rectsAndOffsets: DnDRectsAndOffsets) {
        const { fromRect, fromX, fromY, toRect, toX, toY } = rectsAndOffsets;
        const fromElementCenter = positionUtils().calculateElementMiddlePoint(fromRect);
        const toElementCenter = positionUtils().calculateElementMiddlePoint(toRect);

        const startLeft = Math.floor(fromElementCenter.x + fromX);
        const startTop = Math.floor(fromElementCenter.y + fromY);
        const endLeft = Math.floor(toElementCenter.x + toX);
        const endTop = Math.floor(toElementCenter.y + toY);

        const moveSequence = this.getMoveAbsoluteSequence(startLeft, startTop, endLeft, endTop);
        const startMovePositionActions = this.getMoveActions(startLeft, startTop);
        const pointerDownActions = this.getClickActions(['pointerDown'], LEFT);
        const moveSequenceActions = _.flatMap(moveSequence, movePosition => this.getMoveActions(movePosition.x, movePosition.y));
        const endMovePositionActions = this.getMoveActions(endLeft, endTop);
        const pointerUpActions = this.getClickActions(['pointerUp'], LEFT);
        const actions = startMovePositionActions.concat(pointerDownActions).concat(moveSequenceActions).concat(endMovePositionActions).concat(pointerUpActions);

        return this.actions([{
            type: 'pointer',
            id: 'mouse',
            actions,
        }]);
    }

    public async dragAndDrop(rectsAndOffsets: DnDRectsAndOffsets) {
        if (this.isSafari()) {
            return this.dragAndDropWithActionsAPIWithGeneratedMoves(rectsAndOffsets);
        }
        return this.dragAndDropWithActionsAPI(rectsAndOffsets);
    }

    private doubleClickFallback(element: utils.W3CElement | utils.LegacyElement, eventData: DoubleClickEventData, offsets: ActionOffsets) {
        if (this.isSafari()) {
            return this.doubleClickWithJS(eventData);
        }
        return this.doubleClickWithActionsAPI(element, offsets);
    }

    public async doubleClick(element: utils.W3CElement | utils.LegacyElement, eventData: DoubleClickEventData, offsets: ActionOffsets) {
        if (this.unsupportedActions.has('move') || this.isSafari()) { // doDoubleClick API doesn't work in Safari browser
            return this.doubleClickFallback(element, eventData, offsets);
        }
        try {
            return await this.doDoubleClick(element as utils.W3CElement);
        } catch (err) {
            if (isOldProtocol(err)) {
                this.unsupportedActions.add('move');
                return this.doubleClickFallback(element, eventData, offsets);
            }
            throw err;
        }
    }

    private async dragAndDropWithActionsAPI(rectsAndOffsets: DnDRectsAndOffsets) {
        const { fromRect, fromX, fromY, toRect, toX, toY } = rectsAndOffsets;

        const fromElementCenter = positionUtils().calculateElementMiddlePoint(fromRect);
        const toElementCenter = positionUtils().calculateElementMiddlePoint(toRect);

        const startLeft = Math.floor(fromElementCenter.x + fromX);
        const startTop = Math.floor(fromElementCenter.y + fromY);
        const endLeft = Math.floor(toElementCenter.x + toX);
        const endTop = Math.floor(toElementCenter.y + toY);

        await this.moveWithActionsAPI({ x: startLeft, y: startTop });
        const actionsDown = this.getClickActionList(['pointerDown'], LEFT);
        await this.actions(actionsDown);

        await this.moveWithActionsAPI({ x: endLeft, y: endTop });
        const actionsUp = this.getClickActionList(['pointerUp'], LEFT);
        return this.actions(actionsUp);
    }

    public async getTabIds() {
        const { value } = await this.windowHandles();
        return value;
    }

    public async isVisible(el: utils.W3CElement | utils.LegacyElement) {
        const { value } = await this.elementIdDisplayed(extractElementId(el));
        return value;
    }

    private getSource() { // unused?
        return this.source();
    }

    public async getElementRect(target: SeleniumTarget) {
        const defaultLocation = { width: 0, height: 0, top: 0, left: 0 };
        let loc;
        try {
            loc = await this.getElementLocation(target);
        } catch (err) {
            logger.error('error getting element location', { err });
        }
        if (!loc?.value) {
            return defaultLocation;
        }
        return {
            top: loc.value.y,
            left: loc.value.x,
            width: loc.value.width,
            height: loc.value.height,
        };
    }

    public async end() {
        logger.info('delete session', { sessionId: this.getSessionId() });
        if (!this.getSessionId()) {
            logger.warn('failed to close session because session is undefined');
        }
        this.stopKeepAliveInterval();
        try {
            return await super.end();
        } catch {
            /* !!!!SILENCE!!!! */
            return undefined;
        }
    }

    public async submit(element: utils.W3CElement | utils.LegacyElement) {
        return super.submitFormManually(element as utils.W3CElement);
    }
}
